/**
 * Insurance Market Intelligence Page
 * 
 * Analyze insurance companies based on fleet safety performance.
 * Two views: Historic (by inspection count) and By Fleet Size (truck count).
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield, TrendingUp, TrendingDown, AlertTriangle, Calendar,
  ArrowUpDown, ArrowUp, ArrowDown, Download, RefreshCw, Truck, FileText
} from 'lucide-react';
import { Navbar } from '../components/shared/Navbar';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { Card, MetricCard, CardGrid } from '../components/shared/Card';
import api from '../services/api';

// Types
interface InsuranceStats {
  insurer: string;
  inspections?: number;
  powerUnits?: number;
  drvOosPct: number;
  vehOosPct: number;
  crashes: number;
  fatalities: number;
  injuries: number;
  carriers?: number;
}

interface HistoricResponse {
  success: boolean;
  data: InsuranceStats[];
  meta: { startDate: string; endDate: string; totalInsurers: number };
}

interface ByTruckCountResponse {
  success: boolean;
  data: InsuranceStats[];
  meta: { totalInsurers: number; note?: string };
}

type SortField = 'insurer' | 'inspections' | 'powerUnits' | 'drvOosPct' | 'vehOosPct' | 'crashes' | 'fatalities' | 'injuries';
type SortDirection = 'asc' | 'desc';

// Helper function to format percentage with color coding
const formatOOSRate = (rate: number): { text: string; className: string } => {
  const pct = rate.toFixed(1);
  if (rate < 5) {
    return { text: `${pct}%`, className: 'text-green-600 font-semibold' };
  } else if (rate > 15) {
    return { text: `${pct}%`, className: 'text-red-600 font-semibold' };
  }
  return { text: `${pct}%`, className: 'text-yellow-600' };
};

// Format large numbers
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

export const InsuranceIntelligence: React.FC = () => {
  // Tab state
  const [activeTab, setActiveTab] = useState<'historic' | 'byTruckCount'>('historic');
  
  // Data state
  const [historicData, setHistoricData] = useState<InsuranceStats[]>([]);
  const [truckCountData, setTruckCountData] = useState<InsuranceStats[]>([]);
  const [historicMeta, setHistoricMeta] = useState<HistoricResponse['meta'] | null>(null);
  const [truckCountMeta, setTruckCountMeta] = useState<ByTruckCountResponse['meta'] | null>(null);
  
  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Date filter state (for historic view)
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() - 2);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Sorting state
  const [sortField, setSortField] = useState<SortField>('inspections');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  // Minimum inspections/trucks filter
  const [minCount, setMinCount] = useState(100);

  // Fetch historic data
  const fetchHistoricData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        startDate,
        endDate,
        minInspections: minCount.toString(),
        limit: '100'
      });
      
      const response = await api.get<InsuranceStats[]>(`/market-intelligence/insurance/historic?${params}`);
      
      setHistoricData(response.data);
        setHistoricMeta({ startDate, endDate, totalInsurers: (response.data).length });
    } catch (err: any) {
      console.error('Failed to fetch historic insurance data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, minCount]);

  // Fetch by truck count data
  const fetchByTruckCountData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        minTrucks: minCount.toString(),
        limit: '100'
      });
      
      const response = await api.get<InsuranceStats[]>(`/market-intelligence/insurance/by-truck-count?${params}`);
      
      setTruckCountData(response.data);
        setTruckCountMeta({ totalInsurers: (response.data).length });
    } catch (err: any) {
      console.error('Failed to fetch truck count insurance data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [minCount]);

  // Load data on mount and when dependencies change
  useEffect(() => {
    if (activeTab === 'historic') {
      fetchHistoricData();
    } else {
      fetchByTruckCountData();
    }
  }, [activeTab, fetchHistoricData, fetchByTruckCountData]);

  // Sorted data based on current sort settings
  const sortedData = useMemo(() => {
    const data = activeTab === 'historic' ? [...historicData] : [...truckCountData];
    
    return data.sort((a, b) => {
      let aVal: number | string = 0;
      let bVal: number | string = 0;
      
      switch (sortField) {
        case 'insurer':
          aVal = a.insurer.toLowerCase();
          bVal = b.insurer.toLowerCase();
          break;
        case 'inspections':
          aVal = a.inspections || 0;
          bVal = b.inspections || 0;
          break;
        case 'powerUnits':
          aVal = a.powerUnits || 0;
          bVal = b.powerUnits || 0;
          break;
        case 'drvOosPct':
          aVal = a.drvOosPct;
          bVal = b.drvOosPct;
          break;
        case 'vehOosPct':
          aVal = a.vehOosPct;
          bVal = b.vehOosPct;
          break;
        case 'crashes':
          aVal = a.crashes;
          bVal = b.crashes;
          break;
        case 'fatalities':
          aVal = a.fatalities;
          bVal = b.fatalities;
          break;
        case 'injuries':
          aVal = a.injuries;
          bVal = b.injuries;
          break;
      }
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      
      return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });
  }, [activeTab, historicData, truckCountData, sortField, sortDirection]);

  // Handle column sort click
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Sort icon component
  const SortIcon: React.FC<{ field: SortField }> = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  // Export to CSV
  const handleExport = () => {
    const data = sortedData;
    const isHistoric = activeTab === 'historic';
    
    const headers = [
      'Insurer',
      isHistoric ? 'Inspections' : 'Truck Count',
      'Driver OOS %',
      'Vehicle OOS %',
      'Crashes',
      'Fatalities',
      'Injuries'
    ];
    
    const rows = data.map(row => [
      `"${row.insurer}"`,
      isHistoric ? row.inspections : row.powerUnits,
      row.drvOosPct.toFixed(2),
      row.vehOosPct.toFixed(2),
      row.crashes,
      row.fatalities,
      row.injuries
    ].join(','));
    
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `insurance-intelligence-${activeTab}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const data = sortedData;
    if (data.length === 0) return null;
    
    const totalInspections = data.reduce((sum, r) => sum + (r.inspections || 0), 0);
    const totalTrucks = data.reduce((sum, r) => sum + (r.powerUnits || 0), 0);
    const avgDriverOOS = data.reduce((sum, r) => sum + r.drvOosPct, 0) / data.length;
    const avgVehicleOOS = data.reduce((sum, r) => sum + r.vehOosPct, 0) / data.length;
    const totalCrashes = data.reduce((sum, r) => sum + r.crashes, 0);
    const totalFatalities = data.reduce((sum, r) => sum + r.fatalities, 0);
    
    return {
      totalInspections,
      totalTrucks,
      avgDriverOOS,
      avgVehicleOOS,
      totalCrashes,
      totalFatalities,
      insurerCount: data.length
    };
  }, [sortedData]);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-8 h-8 text-blue-600" />
            Insurance Market Intelligence
          </h1>
          <p className="mt-2 text-gray-600">
            Analyze insurance companies based on the safety performance of their covered fleets.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => {
                  setActiveTab('historic');
                  setSortField('inspections');
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === 'historic'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4" />
                Historic (by Inspections)
              </button>
              <button
                onClick={() => {
                  setActiveTab('byTruckCount');
                  setSortField('powerUnits');
                }}
                className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${
                  activeTab === 'byTruckCount'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Truck className="w-4 h-4" />
                By Fleet Size (Truck Count)
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <div className="flex flex-wrap items-end gap-4">
            {activeTab === 'historic' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min {activeTab === 'historic' ? 'Inspections' : 'Trucks'}
              </label>
              <input
                type="number"
                value={minCount}
                onChange={(e) => setMinCount(parseInt(e.target.value) || 0)}
                min={0}
                className="w-32 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <button
              onClick={() => activeTab === 'historic' ? fetchHistoricData() : fetchByTruckCountData()}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>

            <button
              onClick={handleExport}
              disabled={sortedData.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              Export CSV
            </button>
          </div>
        </Card>

        {/* Summary Stats */}
        {summaryStats && !loading && (
          <CardGrid columns={4} className="mb-6">
            <MetricCard
              title="Insurers Analyzed"
              value={summaryStats.insurerCount}
              icon={<Shield className="w-6 h-6" />}
              color="blue"
            />
            <MetricCard
              title={activeTab === 'historic' ? 'Total Inspections' : 'Total Trucks'}
              value={formatNumber(activeTab === 'historic' ? summaryStats.totalInspections : summaryStats.totalTrucks)}
              icon={activeTab === 'historic' ? <FileText className="w-6 h-6" /> : <Truck className="w-6 h-6" />}
              color="gray"
            />
            <MetricCard
              title="Avg Driver OOS %"
              value={`${summaryStats.avgDriverOOS.toFixed(1)}%`}
              icon={<TrendingUp className="w-6 h-6" />}
              color={summaryStats.avgDriverOOS < 5 ? 'green' : summaryStats.avgDriverOOS > 15 ? 'red' : 'yellow'}
            />
            <MetricCard
              title="Total Fatalities"
              value={summaryStats.totalFatalities.toLocaleString()}
              icon={<AlertTriangle className="w-6 h-6" />}
              color="red"
            />
          </CardGrid>
        )}

        {/* Error State */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">Error:</span>
              <span>{error}</span>
            </div>
          </div>
        )}

        {/* Data Table */}
        <Card>
          {loading ? (
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" message="Loading insurance data..." />
            </div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Shield className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-lg font-medium">No data available</p>
              <p className="text-sm mt-1">Try adjusting your filters or date range.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('insurer')}
                    >
                      <div className="flex items-center gap-2">
                        Insurer
                        <SortIcon field="insurer" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort(activeTab === 'historic' ? 'inspections' : 'powerUnits')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        {activeTab === 'historic' ? 'Inspections' : 'Truck Count'}
                        <SortIcon field={activeTab === 'historic' ? 'inspections' : 'powerUnits'} />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('drvOosPct')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Driver OOS %
                        <SortIcon field="drvOosPct" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('vehOosPct')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Vehicle OOS %
                        <SortIcon field="vehOosPct" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('crashes')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Crashes
                        <SortIcon field="crashes" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('fatalities')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Fatalities
                        <SortIcon field="fatalities" />
                      </div>
                    </th>
                    <th 
                      className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('injuries')}
                    >
                      <div className="flex items-center justify-end gap-2">
                        Injuries
                        <SortIcon field="injuries" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {sortedData.map((row, idx) => {
                    const driverOOS = formatOOSRate(row.drvOosPct);
                    const vehicleOOS = formatOOSRate(row.vehOosPct);
                    
                    return (
                      <tr key={row.insurer} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">
                          {row.insurer}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">
                          {formatNumber(activeTab === 'historic' ? (row.inspections || 0) : (row.powerUnits || 0))}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right ${driverOOS.className}`}>
                          {driverOOS.text}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right ${vehicleOOS.className}`}>
                          {vehicleOOS.text}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700 text-right">
                          {row.crashes.toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right ${row.fatalities > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>
                          {row.fatalities.toLocaleString()}
                        </td>
                        <td className={`px-4 py-3 text-sm text-right ${row.injuries > 0 ? 'text-orange-600' : 'text-gray-700'}`}>
                          {row.injuries.toLocaleString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Legend */}
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-600">
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-green-500"></span>
            <span>OOS Rate &lt; 5% (Good)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
            <span>OOS Rate 5-15% (Average)</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-full bg-red-500"></span>
            <span>OOS Rate &gt; 15% (Poor)</span>
          </div>
        </div>
      </main>
    </div>
  );
};

export default InsuranceIntelligence;
